import { supabase } from '../lib/supabase';

// ── Error types ──────────────────────────────────────────────
// AI_DAILY_LIMIT (429): today's cap (Guatemala day) is spent — never auto-retried.
export type AIErrorCode = 'AI_NO_SUB' | 'AI_QUOTA_EXCEEDED' | 'AI_DAILY_LIMIT' | 'AI_RATE_LIMITED' | 'AI_PROVIDER_ERROR' | 'AI_AUTH_ERROR' | 'AI_BAD_REQUEST' | 'AI_TIMEOUT';
const AI_ERROR_CODES: readonly string[] = ['AI_NO_SUB', 'AI_QUOTA_EXCEEDED', 'AI_DAILY_LIMIT', 'AI_RATE_LIMITED', 'AI_PROVIDER_ERROR', 'AI_AUTH_ERROR', 'AI_BAD_REQUEST', 'AI_TIMEOUT'];

export class AIError extends Error {
  code: AIErrorCode;
  details?: Record<string, any>;

  constructor(code: AIErrorCode, message: string, details?: Record<string, any>) {
    super(message);
    this.code = code;
    this.details = details;
    this.name = 'AIError';
  }
}

// ── Usage info returned with every successful call ───────────
export interface AIUsageInfo {
  tokens_used: number;
  period_total: number;
  period_limit: number;
}

// ── Internal helper ──────────────────────────────────────────
async function invokeAIProxy<T = any>(
  action: string,
  payload: Record<string, any>
): Promise<{ result: T; usage: AIUsageInfo }> {
  if (!supabase) throw new Error('Supabase no configurado');

  const { data, error } = await supabase.functions.invoke('ai-proxy', {
    body: { action, payload },
  });

  if (error) {
    // Non-2xx responses (403 AI_NO_SUB, 429 quota/rate-limit, 401 auth) throw
    // FunctionsHttpError with data=null — recover the guard's JSON body from the
    // Response so differentiated error codes actually reach the UI.
    const body = await (error as any).context?.json?.().catch(() => null);
    const code = body?.code as AIErrorCode | undefined;
    if (code && AI_ERROR_CODES.includes(code)) {
      throw new AIError(code, body.error || 'Error de IA', body);
    }
    throw new AIError('AI_PROVIDER_ERROR', error.message || 'Error de conexión con IA');
  }

  // Check for application-level error codes
  if (data?.code) {
    const code = data.code as AIErrorCode;
    if (AI_ERROR_CODES.includes(code)) {
      throw new AIError(code, data.error || 'Error de IA', data);
    }
  }

  const usage: AIUsageInfo = data?.usage || { tokens_used: 0, period_total: 0, period_limit: 0 };
  return { result: data as T, usage };
}

// ── Leaked tool-call markup guard ─────────────────────────────
// Mirror of the ai-proxy server guard: some providers (DeepSeek) occasionally
// emit their internal function-calling markup (e.g. "<｜｜DSML｜｜tool_calls>")
// as plain text. It must never be shown NOR saved into the chat history /
// sessionStorage — the model imitates it on later turns once it's in history.
const TOOL_MARKUP_MARKERS = ['<｜', '｜DSML', '<|DSML', '<|tool'];

export function stripToolMarkup(text: string): string {
  let cut = -1;
  for (const mk of TOOL_MARKUP_MARKERS) {
    const i = text.indexOf(mk);
    if (i !== -1 && (cut === -1 || i < cut)) cut = i;
  }
  return cut === -1 ? text : text.slice(0, cut).trimEnd();
}

// ── Chat protocol types (tools mode) ─────────────────────────
export interface ChatToolCall {
  id: string;
  type: 'function';
  function: { name: string; arguments: string };
}

export interface ChatMessage {
  role: 'user' | 'assistant' | 'tool';
  content: string | null;
  tool_calls?: ChatToolCall[];
  tool_call_id?: string;
}

// ── Public API (same signatures as geminiService) ────────────

// Client-side stall guard (A10): no first byte within 45 s, or 30 s of silence
// between chunks, aborts the request as AI_TIMEOUT instead of spinning forever.
export const FIRST_EVENT_TIMEOUT_MS = 45_000;
export const IDLE_TIMEOUT_MS = 30_000;

/** Fires `onTimeout` after `firstMs`, or `idleMs` after the latest kick(). */
export function createIdleWatchdog(onTimeout: () => void, firstMs: number, idleMs: number) {
  let timer = setTimeout(onTimeout, firstMs);
  return {
    kick() { clearTimeout(timer); timer = setTimeout(onTimeout, idleMs); },
    stop() { clearTimeout(timer); },
  };
}

/**
 * Streaming chat: returns text deltas and tool calls via callbacks.
 * Uses a direct fetch (not supabase.functions.invoke) so the response
 * body can be read as a ReadableStream of SSE events.
 * `signal` is the caller's per-turn Stop: aborting it rethrows the AbortError
 * (the caller checks `signal.aborted`); a stall throws AI_TIMEOUT.
 */
export const sendChatStream = async (
  messages: ChatMessage[],
  ctx: { activeView?: string; now?: string; snapshot?: unknown; screenContext?: unknown; assistantName?: string },
  callbacks: {
    onTextDelta: (delta: string) => void;
    onToolCalls: (toolCalls: ChatToolCall[]) => void;
    onDone: (usage?: AIUsageInfo) => void;
  },
  signal?: AbortSignal,
): Promise<void> => {
  if (!supabase) throw new AIError('AI_PROVIDER_ERROR', 'Supabase no configurado');

  const session = await supabase.auth.getSession();
  const token = session.data.session?.access_token;
  if (!token) throw new AIError('AI_AUTH_ERROR', 'Sesión expirada');

  // One controller per request, aborted by the caller's Stop or the watchdog.
  const ctrl = new AbortController();
  let timedOut = false;
  const onStop = () => ctrl.abort();
  if (signal?.aborted) ctrl.abort();
  else signal?.addEventListener('abort', onStop, { once: true });
  const watchdog = createIdleWatchdog(() => { timedOut = true; ctrl.abort(); }, FIRST_EVENT_TIMEOUT_MS, IDLE_TIMEOUT_MS);

  try {
    const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-proxy`;
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({
        action: 'chat',
        payload: {
          messages,
          activeView: ctx.activeView || 'dashboard',
          now: ctx.now || new Date().toISOString(),
          snapshot: ctx.snapshot || null,
          screenContext: ctx.screenContext || null,
          platform: 'web',
          assistantName: ctx.assistantName || undefined,
          stream: true,
        },
      }),
      signal: ctrl.signal,
    });

    if (!res.ok) {
      const body = await res.json().catch(() => null);
      const code = body?.code as AIErrorCode | undefined;
      if (code && AI_ERROR_CODES.includes(code)) {
        throw new AIError(code, body.error || 'Error de IA', body);
      }
      throw new AIError('AI_PROVIDER_ERROR', 'Error de conexión con IA');
    }

    if (!res.body) throw new AIError('AI_PROVIDER_ERROR', 'No response body');

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      watchdog.kick();
      buffer += decoder.decode(value, { stream: true });

      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed.startsWith('data: ')) continue;
        const data = trimmed.slice(6);
        if (data === '[DONE]') { callbacks.onDone(); return; }

        try {
          const event = JSON.parse(data);
          if (event.type === 'text_delta') callbacks.onTextDelta(event.content);
          else if (event.type === 'tool_calls') callbacks.onToolCalls(event.tool_calls);
          else if (event.type === 'done') { callbacks.onDone(event.usage); return; }
          else if (event.type === 'error') {
            // The edge tags provider timeouts as AI_TIMEOUT; anything else is a provider error.
            const code: AIErrorCode = AI_ERROR_CODES.includes(event.code) ? event.code : 'AI_PROVIDER_ERROR';
            throw new AIError(code, event.error || 'Error de IA', event);
          }
        } catch (e) {
          if (e instanceof AIError) throw e;
          // skip unparseable
        }
      }
    }

    callbacks.onDone();
  } catch (e) {
    if (timedOut) throw new AIError('AI_TIMEOUT', 'La respuesta tardó demasiado');
    if (e instanceof AIError || signal?.aborted) throw e;
    // Network drop (fetch/read rejected) — same handling as a provider error.
    throw new AIError('AI_PROVIDER_ERROR', 'Error de conexión con IA');
  } finally {
    watchdog.stop();
    signal?.removeEventListener('abort', onStop);
  }
};

/**
 * Conversational chat with Aura AI assistant (tool-calling protocol).
 * Sends the running message history; the server decides whether to answer
 * with text or request client-side tool executions.
 */
export const sendChat = async (
  messages: ChatMessage[],
  ctx: { activeView?: string; now?: string; snapshot?: unknown; screenContext?: unknown }
): Promise<{ text: string; toolCalls: ChatToolCall[] | null; finishReason?: string; usage?: AIUsageInfo }> => {
  const { result, usage } = await invokeAIProxy<{ text?: string; tool_calls?: ChatToolCall[] | null; finish_reason?: string }>('chat', {
    messages,
    activeView: ctx.activeView || 'dashboard',
    now: ctx.now || new Date().toISOString(),
    snapshot: ctx.snapshot || null,
    screenContext: ctx.screenContext || null,
  });
  return {
    text: result.text || '',
    toolCalls: result.tool_calls && result.tool_calls.length > 0 ? result.tool_calls : null,
    finishReason: result.finish_reason,
    usage,
  };
};

// ── Property extraction ───────────────────────────────────────
export interface ExtractedPropertyDetails {
  title?: string;
  price?: number;
  operation?: 'sale' | 'rent';
  type?: string;
  /** Structured location (v2). Legacy responses may return a plain string. */
  location?: { department?: string; zone_or_city?: string; subsector?: string } | string;
  specs?: { beds?: number; baths?: number; area?: number; parking?: number; yearBuilt?: number };
  description?: string;
  amenities?: string[];
  price_label?: string;
  /** Monthly maintenance fee — always Quetzales (Q). */
  maintenance?: number;
}

/**
 * Extracts property details from free-text input. `knownTypes` /
 * `knownAmenities` constrain the model to the caller's lookup catalogs so
 * the returned names resolve to real IDs.
 */
export const extractPropertyDetails = async (
  text: string,
  opts?: { knownTypes?: string[]; knownAmenities?: string[] }
): Promise<ExtractedPropertyDetails> => {
  const { result } = await invokeAIProxy<{ data: any }>('extract_property', {
    text,
    known_types: opts?.knownTypes,
    known_amenities: opts?.knownAmenities,
  });
  return result.data || {};
};

/**
 * Enhances a CRM entry description to be more professional.
 */
export const enhanceDescription = async (title: string, currentDescription: string): Promise<string> => {
  const { result } = await invokeAIProxy<{ text: string }>('enhance_description', {
    title,
    description: currentDescription,
  });
  return result.text || currentDescription;
};

/**
 * "Resumen de la semana" — fetches role-scoped weekly metrics (RPC) and returns an es-GT narrative.
 */
export const getWeeklySummary = async (): Promise<{ text: string; usage?: AIUsageInfo }> => {
  if (!supabase) throw new Error('Supabase no configurado');
  const to = new Date();
  const from = new Date(to.getTime() - 7 * 24 * 60 * 60 * 1000);
  const { data, error } = await supabase.rpc('get_ai_weekly_summary', { p_from: from.toISOString(), p_to: to.toISOString() });
  if (error) throw new AIError('AI_PROVIDER_ERROR', error.message || 'No se pudo obtener el resumen semanal');
  const { result, usage } = await invokeAIProxy<{ text: string }>('weekly_summary', { summary: data });
  return { text: result.text, usage };
};

/**
 * "Pendientes de esta semana" — fetches the caller's pending reminders + due follow-ups (RPC) and returns an es-GT to-do list.
 */
export const getWeekTodo = async (): Promise<{ text: string; usage?: AIUsageInfo }> => {
  if (!supabase) throw new Error('Supabase no configurado');
  const { data, error } = await supabase.rpc('get_ai_week_todo', {});
  if (error) throw new AIError('AI_PROVIDER_ERROR', error.message || 'No se pudieron obtener los pendientes');
  // Up to 40 items: send only what the narration needs so the body stays under
  // ai-proxy's 12k non-chat cap (notes clipped).
  const raw = data as any;
  const todo = {
    overdue_count: raw?.overdue_count,
    upcoming_count: raw?.upcoming_count,
    items: (Array.isArray(raw?.items) ? raw.items : []).map((t: any) => ({
      kind: t.kind,
      title: t.title ?? null,
      contact_name: t.contact_name ?? null,
      due_local: t.due_local ?? t.due_at,
      overdue: t.overdue,
      note: t.note ? String(t.note).slice(0, 120) : null,
    })),
  };
  const { result, usage } = await invokeAIProxy<{ text: string }>('week_todo', { todo });
  return { text: result.text, usage };
};
